import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import {
    ChartData,
    ChartDataset,
    Chart as ChartJS,
    ChartOptions,
    Legend,
    LinearScale,
    PointElement,
    ScatterDataPoint,
    Title,
    Tooltip,
} from "chart.js";
import { useContext, useEffect, useRef, useState } from "react";
import { Scatter } from "react-chartjs-2";
import { useLocation, useNavigate } from "react-router-dom";
import { APIContext } from "../../Contexts/APIContext";
import { SessionContext } from "../../Contexts/SessionContext";
import ExportRankOrderScatterPlotDialog from "../../TripRate/RankOrderList/ExportRankOrderScatterPlotDialog";
import ExportRankOrderScatterPlotViewModel from "../../TripRate/ViewModels/ExportRankOrderScatterPlotViewModel";
import { TimePair } from "../../Utilities/ViewModels/TimePair";
import Directions from "../Directions";
import { navigateToSite } from "../../Site/SiteNavigation";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, Title);


function linearRegression(data: ScatterDataPoint[]) {
    const n = data.length;
    if (!n) return { slope: 0, intercept: 0 };
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (const { x, y } of data) {
        sumX += x!;
        sumY += y!;
        sumXY += x! * y!;
        sumXX += x! * x!;
    }
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept };
}

function getAxisBounds(min: number | undefined, max: number | undefined) {
    if (min === undefined || max === undefined || !Number.isFinite(min) || !Number.isFinite(max)) {
        return { min: undefined, max: undefined };
    }

    // Keep points away from the plot boundary and leave the outermost labelled
    // ticks inside the data range, matching the presentation used by TRICS 7.
    const range = max - min;
    const padding = range > 0
        ? range * 0.05
        : Math.max(Math.abs(min) * 0.05, 1);

    return { min: min - padding, max: max + padding };
}

function getNiceAxisStep(value: number) {
    const magnitude = 10 ** Math.floor(Math.log10(value));
    const fraction = value / magnitude;

    if (fraction < 1.5) return magnitude;
    if (fraction < 3) return 2 * magnitude;
    if (fraction < 7) return 5 * magnitude;
    return 10 * magnitude;
}

function getXAxisBounds(min: number | undefined, max: number | undefined) {
    if (min === undefined || max === undefined || !Number.isFinite(min) || !Number.isFinite(max)) {
        return { min: undefined, max: undefined, stepSize: undefined };
    }

    const range = max - min;
    if (range <= 0) {
        const padding = Math.max(Math.abs(min) * 0.05, 1);
        return { min: min - padding, max: max + padding, stepSize: undefined };
    }

    // Base the scale on the spread of the selected data, rather than its
    // absolute maximum. Aim for roughly ten rounded intervals, as in TRICS 7.
    const stepSize = getNiceAxisStep(range / 10);
    return {
        min: Math.floor(min / stepSize) * stepSize,
        max: Math.ceil(max / stepSize) * stepSize,
        stepSize,
    };
}

interface IProps {
    pair: TimePair;
    vehicleTypeId: number;
    vehicleTypeLabel: string;
    calculation: string;
    timeRange: string;
    clickToggle: string;
    surveyRefs: string[];
}

export default function RankOrderScatterPlot({
    pair,
    vehicleTypeId,
    vehicleTypeLabel,
    calculation,
    timeRange,
    clickToggle,
    surveyRefs,
}: IProps) {
    const { post, loading, postPDF } = useContext(APIContext);
    const { sessionState } = useContext(SessionContext);
    const navigate = useNavigate();
    const location = useLocation();
    const chartRef = useRef<ChartJS<"scatter">>(null);

    const [data, setData] = useState<(ScatterDataPoint & { label: string })[]>([]);
    const [direction, setDirection] = useState("Total");
    const [dialogOpen, setDialogOpen] = useState(false);
    const surveyRefsKey = JSON.stringify(surveyRefs);
    const dataXValues = data.map((point) => point.x).filter((value): value is number => value !== null);
    const dataYValues = data.map((point) => point.y).filter((value): value is number => value !== null);
    const actualXMin = dataXValues.length ? Math.min(...dataXValues) : sessionState.primaryFilters.actualMinHeader ?? undefined;
    const actualXMax = dataXValues.length ? Math.max(...dataXValues) : sessionState.primaryFilters.actualMaxHeader ?? undefined;
    const actualYMin = dataYValues.length ? Math.min(...dataYValues) : undefined;
    const actualYMax = dataYValues.length ? Math.max(...dataYValues) : undefined;
    const { min: xAxisMin, max: xAxisMax, stepSize: xAxisStepSize } = getXAxisBounds(actualXMin, actualXMax);
    const { min: yAxisMin, max: yAxisMax } = getAxisBounds(actualYMin, actualYMax);

    const resolveSurveyRef = (siteRef: string) => {
        const matchingSurveys = sessionState.selectedSitesAndSurveys.filter((survey) =>
            survey.siteActive &&
            survey.surveyActive &&
            survey.siteRef === siteRef
        );

        if (matchingSurveys.length === 1) {
            return matchingSurveys[0].surveyRef;
        }

        return matchingSurveys[0]?.surveyRef ?? siteRef;
    };

    const getGraphReturnTo = () => {
        const params = new URLSearchParams(location.search);
        params.set("toTab", "rankGraph");
        params.set("selectionType", "site");
        const search = params.toString();

        return `${location.pathname}${search ? `?${search}` : ""}${location.hash}`;
    };

    useEffect(() => {
        const requestSurveyRefs = JSON.parse(surveyRefsKey) as string[];
        if (requestSurveyRefs.length === 0) {
            setData([]);
            return;
        }

        post(
            "Chart",
            `GetRankOrderScatterPlot?headerIndex=${sessionState.primaryFilters.headerIndex}&direction=${direction}&start=${pair.start}&end=${pair.end}&vehicleType=${vehicleTypeId}`,
            requestSurveyRefs,
            setData
        );
    }, [direction, vehicleTypeId, pair.start, pair.end, sessionState.primaryFilters.headerIndex, surveyRefsKey]);

    const chartData: ChartData<"scatter", ScatterDataPoint[]> = {
        datasets: (() => {
            const ds: ChartDataset<"scatter", ScatterDataPoint[]> = {
                label: `${vehicleTypeLabel} vs ${calculation}`,
                data,
                pointRadius: 7,
                pointStyle: "circle",
                pointBackgroundColor: "#0188ac",
                pointBorderColor: "#ffffff",
                pointBorderWidth: 2,
                showLine: false,
            };

            if (data.length > 1) {
                const { slope, intercept } = linearRegression(data);
                const xs = data.map((pt) => pt.x!);
                const [minX, maxX] = [Math.min(...xs), Math.max(...xs)];
                const lineData: ScatterDataPoint[] = [
                    { x: minX, y: slope * minX + intercept },
                    { x: maxX, y: slope * maxX + intercept },
                ];
                const lineDs: ChartDataset<"scatter", ScatterDataPoint[]> = {
                    label: "Line of Best Fit",
                    data: lineData,
                    borderColor: "red",
                    borderWidth: 2,
                    pointRadius: 0,
                    showLine: true,
                    fill: false,
                    order: 0,
                };
                return [ds, lineDs];
            }

            return [ds];
        })(),
    };

    const options: ChartOptions<"scatter"> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: `SCATTER PLOT - ${direction}   Time Range: ${timeRange}   ${vehicleTypeLabel}   CALCULATION FACTOR ${calculation}`,
                font: { size: 14 },
                padding: { top: 10, bottom: 30 },
            },
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        const pt = tooltipItems[0].raw as { label: string };
                        return pt.label;
                    },
                    label: (tooltipItem) => {
                        const { x, y } = tooltipItem.parsed;
                        if (x === null || y === null) return "";
                        const xValue = x.toFixed(2);
                        const yValue = y.toFixed(2);
                        return `${calculation} = ${xValue}, ${vehicleTypeLabel} = ${yValue}`;
                    },
                },
            },
        },
        scales: {
            x: {
                type: "linear",
                offset: true,
                title: {
                    display: true,
                    text: `${calculation}`,
                    font: { size: 16 },
                },
                min: xAxisMin,
                max: xAxisMax,
                ticks: { stepSize: xAxisStepSize },
                grid: { color: "#ccc" },
            },
            y: {
                type: "linear",
                title: {
                    display: true,
                    text: `${vehicleTypeLabel}`,
                    font: { size: 16 },
                },
                min: yAxisMin,
                max: yAxisMax,
                afterBuildTicks: (axis) => {
                    if (actualYMin === undefined || actualYMax === undefined) return;
                    axis.ticks = axis.ticks.filter((tick) => tick.value > actualYMin && tick.value < actualYMax);
                },
                grid: { color: "#ccc" },
            },
        },
        onClick: (evt, elems, chart) => {
            if (elems.length) {
                const { datasetIndex, index } = elems[0];
                const dp = (chart.data.datasets[datasetIndex] as ChartDataset<
                    "scatter",
                    any
                >).data[index] as any;
                if (clickToggle === "site")
                    navigateToSite(navigate, location, {
                        siteRef: dp.label,
                        type: "rankorder",
                        fromTab: "rankGraph",
                        returnLabel: "Back to Graph",
                        returnTo: getGraphReturnTo()
                    });
                else
                    navigate(`/triprate/results?selectedSurvey=${encodeURIComponent(resolveSurveyRef(dp.label))}&fromTab=rankGraph`);
            }
        },
    };

    const openDialog = () => setDialogOpen(true);
    const closeDialog = () => setDialogOpen(false);

    const exportJPEG = () => {
        const chart = chartRef.current;
        if (!chart) return;

        const source = chart.canvas;
        const output = document.createElement("canvas");
        output.width = source.width;
        output.height = source.height;
        const context = output.getContext("2d");
        if (!context) return;

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, output.width, output.height);
        context.drawImage(source, 0, 0);

        const a = document.createElement("a");
        a.href = output.toDataURL("image/jpeg", 1.0);
        a.download = `RankOrderScatterPlot_${vehicleTypeLabel}.jpeg`;
        a.click();
    };

    const getExport = (vm: ExportRankOrderScatterPlotViewModel) => {
        if (vm.exportType === "JPEG") {
            exportJPEG();
            closeDialog();
            return;
        }
        vm.scatterPlotData = data.map((d) => ({
            x: d.x!,
            y: d.y!,
            label: d.label,
        }));

        vm.selectedRange = timeRange;
        vm.direction = direction;
        vm.calculationFactor = calculation;
        vm.vehicleType = vehicleTypeLabel;
        vm.xAxisMin = xAxisMin ?? null;
        vm.xAxisMax = xAxisMax ?? null;
        vm.xAxisStepSize = xAxisStepSize ?? null;
        vm.yAxisMin = yAxisMin ?? null;
        vm.yAxisMax = yAxisMax ?? null;

        postPDF("PDFGenerator", "GetRankOrderScatterPlotPDF", vm.fileName ?? "Rank Order Scatter Plot", vm.displayToScreen, vm);
        closeDialog();
    };

    return (
        <Paper>
            <Grid container>
                <Grid size={12} sx={{ textAlign: "-webkit-center" }}>
                    <Directions setType={setDirection} type={direction} />
                </Grid>
                <Grid>
                    <Button onClick={openDialog}>Export</Button>
                </Grid>
                {loading ? (
                    <Grid size={12} sx={{ textAlign: "-webkit-center" }}>
                        <CircularProgress />
                    </Grid>
                ) : (
                    <Grid size={12} sx={{ height: 650, minHeight: 650 }}>
                        <Scatter ref={chartRef} data={chartData} options={options} />
                    </Grid>
                )}
            </Grid>
            {dialogOpen && (<ExportRankOrderScatterPlotDialog dialogOpen={dialogOpen} handleClose={closeDialog} exportFiltersViewModel={getExport} />
            )}
        </Paper>
    );
}
